import { useLocalStorage } from 'react-use';
import { createContext, type PropsWithChildren, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { type SignInModalProps, useAuth } from '@ev-tech/user-account-modules';
import { type ModalState, useModalState } from '@ev-tech/ui-core-components/Modal';
import { type SelfDescribingJson } from '@ev-tech/snowplow-library';
import { type Language, type MarketCountryCode } from '@ev-tech/search-modules-api';

import { type TFunction, type TransComponent } from './AppContextProvider';
import { safeSessionStorage } from '../utils/storageUtils';
import { captureScrollForAuth, restorePersistedAuthScrollIfPresent } from '../utils/preserveScrollDuringAuth';
import { type PendingUserAccountItem, type UserAccountProps } from '../types/types';
import { usePendingUAExclusiveExposeAfterAuth } from '../hooks/usePendingUAExclusiveExposeAfterAuth';
import { MAP_VIEW_RETURN_SESSION_KEY, PENDING_USER_ACCOUNT_ITEM_KEY } from '../consts/consts';
import { UserAccountModal } from '../components/UserAccountModal/UserAccountModal';

export type OpenUserAccountModalParams = {
  signInTrigger?: SignInModalProps['trigger'];
  triggerId?: string;
  additionalTrackingContexts?: SelfDescribingJson[];
};

export type UserAccountContextType = {
  state: ModalState;
  /** Sets the sign-in dialog trigger and its source element ID, then opens the user account modal. */
  openUserAccountModal: (props?: OpenUserAccountModalParams) => void;
};
const initialStore: UserAccountContextType = {
  state: { isOpen: false, setOpen: () => {}, open: () => {}, close: () => {}, toggle: () => {} },
  openUserAccountModal: () => {},
};

export const UserAccountContext = createContext<UserAccountContextType>(initialStore);

export type UserAccountProviderProps = Partial<UserAccountProps> & {
  countryCode: MarketCountryCode;
  language: Language;
  transComponent: TransComponent;
  translationResolver: TFunction;
  tracking?: SignInModalProps['tracking'];
};

export const UserAccountProvider = ({
  children,
  countryCode,
  language,
  transComponent,
  translationResolver,
  tracking,
}: PropsWithChildren<UserAccountProviderProps>) => {
  const [_, __, removePendingItemToSave] = useLocalStorage<PendingUserAccountItem | null>(PENDING_USER_ACCOUNT_ITEM_KEY);
  const userAccountModalState = useModalState();
  const { isSignedIn } = useAuth();

  const signInTriggerRef = useRef<SignInModalProps['trigger'] | undefined>(undefined);
  const triggerIdRef = useRef<string | undefined>(undefined);
  const additionalTrackingContextsRef = useRef<SelfDescribingJson[]>([]);

  usePendingUAExclusiveExposeAfterAuth(userAccountModalState.isOpen);

  useLayoutEffect(() => {
    restorePersistedAuthScrollIfPresent();
  }, []);

  const handleUserAccountModalOpenChange = useCallback(
    (isOpen: boolean) => {
      userAccountModalState.setOpen(isOpen);
      if (!isOpen) {
        signInTriggerRef.current = undefined;
        if (isSignedIn) {
          return;
        }
        safeSessionStorage.removeItem(MAP_VIEW_RETURN_SESSION_KEY);
        triggerIdRef.current = undefined;
        additionalTrackingContextsRef.current = [];
        removePendingItemToSave();
      }
    },
    [isSignedIn, removePendingItemToSave, userAccountModalState],
  );

  const closeUserAccountModal = useCallback(() => {
    handleUserAccountModalOpenChange(false);
  }, [handleUserAccountModalOpenChange]);

  const modalStateWithCleanup = useMemo<ModalState>(
    () => ({
      ...userAccountModalState,
      close: closeUserAccountModal,
      setOpen: handleUserAccountModalOpenChange,
    }),
    [userAccountModalState, closeUserAccountModal, handleUserAccountModalOpenChange],
  );

  const openUserAccountModal = useCallback(
    ({ signInTrigger, triggerId, additionalTrackingContexts = [] }: OpenUserAccountModalParams = {}) => {
      captureScrollForAuth();
      signInTriggerRef.current = signInTrigger;
      triggerIdRef.current = triggerId;
      additionalTrackingContextsRef.current = additionalTrackingContexts;
      userAccountModalState.open();
    },
    [userAccountModalState],
  );

  return (
    <UserAccountContext.Provider value={{ state: modalStateWithCleanup, openUserAccountModal }}>
      {children}
      {userAccountModalState.isOpen && (
        <UserAccountModal
          modalState={modalStateWithCleanup}
          countryCode={countryCode}
          language={language}
          t={translationResolver}
          transComponent={transComponent}
          trigger={signInTriggerRef.current}
          triggerId={triggerIdRef.current}
          tracking={{
            ...tracking,
            pageType: tracking?.pageType ?? '',
            templateType: tracking?.templateType ?? '',
            shopId: tracking?.shopId ?? '',
            contexts: [...(tracking?.contexts ?? []), ...additionalTrackingContextsRef.current],
          }}
          onOpenChange={handleUserAccountModalOpenChange}
        />
      )}
    </UserAccountContext.Provider>
  );
};
